import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { isSubmitting, submit } from 'redux-form';
import capitalize from 'capitalize';
import groupBy from 'lodash/groupBy';
import { BounceLoader } from 'halogenium';
import styled from 'styled-components';
import Icon, { LoadingOutlined } from '@ant-design/icons';

import './RequestMaintenance.css';

import FlatButton from 'material-ui/FlatButton';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import TicketStatusIcon from 'components/shared/TenantTicketStatus';

import { toFriendlyDateString, timeFromNow } from 'helpers/Formatter';
import { hostIsBoardwalk } from 'helpers/CurrentWindowLocation';

import { localize } from 'react-localize-redux';

import { openMaintenanceDialog, getTenancyStatus } from 'actions/dashboardActions';
import { getMaintenanceTicket, saveMaintenanceTicket } from 'actions/tenantMaintenanceActions';
import * as maintenanceActions from 'actions/maintenanceActions';
import { getTenantMaintenanceRequest } from 'actions/tenantMaintenanceActions';
import * as dropzoneMultiFileActions from 'actions/dropzoneMultiFileActions';
import { colors } from 'helpers/ColorHelper';
import { AccentColor } from 'helpers/AccentColorHelper';
import { serviceRequestOptions } from 'helpers/VerbiageHelper';
import { getTranslatedString } from 'helpers/i18n';

import TenantMaintenanceDialog from '../TenantMaintenanceDialog';
import { RenderIfToggleOn } from 'components/shared/ToggleRenderer';

const DialogSubHeaders = styled.h4`
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
`;

const OutstandingMaintenance = withMobileDialog()(
  connect(
    (store) => ({
      createMaintenanceRequest: store.dashboard.createMaintenanceRequest,
      maintenanceTickets: store.tenantMaintenance.tickets,
      maintenanceTicket: store.tenantMaintenance.ticket,
      loadingTickets: store.tenantMaintenance.loadingTickets,
      submitting: isSubmitting('new_tenant_ticket_form')(store),
      lightboxIsOpen: store.dropzoneMultiLine.lightboxIsOpen,
      currentLanguage: store.locale.currentLanguage,
      propertyTypes: store.userTypes.propertyTypes,
      userPreferences: store.userTypes.userPreferences,
    }),
    {
      openMaintenanceDialog,
      ...maintenanceActions,
      getTenantMaintenanceRequest,
      submit,
      getTenancyStatus,
      getMaintenanceTicket,
      saveMaintenanceTicket,
      ...dropzoneMultiFileActions,
    }
  )(
    class OutstandingMaintenance extends Component {
      constructor(props, context) {
        super(props, context);
        this.state = {
          maintenanceTicketDetails: {},
          maintenanceDialogIsOpen: false,
        };
      }

      componentDidMount = () => {
        this.props.getTenantMaintenanceRequest();
      };

      openDialog = () => {
        this.props.openMaintenanceRequest();
      };

      closeMaintenanceDialog = () => {
        if (this.props.lightboxIsOpen) {
          this.props.closeLightbox();
          return;
        }
        this.props.saveMaintenanceTicket(null);
        this.setState({
          maintenanceDialogIsOpen: false,
        });
      };

      openMaintenanceDialog = (ticket) => {
        if (!ticket) return;
        this.props.getMaintenanceTicket(ticket.id);
        this.setState({
          maintenanceDialogIsOpen: true,
        });
      };

      updateMaintenanceTicket = (formData) => {
        this.props.createOrUpdateMaintenanceTicket(formData, formData.id).then(() => {
          this.props.getTenantMaintenanceRequest();
        });
      };

      cancelMaintenanceTicket = async (ticketID) => {
        this.props.toggleDialog();
        await this.props.cancelMaintenanceTicket(ticketID);
        await this.props.getTenantMaintenanceRequest();
        this.props.getTenancyStatus();
      };

      render() {
        return (
          <Fragment>
            <TenantMaintenanceDialog
              ticketInfo={this.props.maintenanceTicket}
              initialValues={this.props.maintenanceTicket}
              submitMaintenance={() => this.props.submit('tenant_update_maintenance')}
              onSubmit={this.updateMaintenanceTicket}
              cancelMaintenanceTicket={this.cancelMaintenanceTicket}
              isOpen={this.state.maintenanceDialogIsOpen}
              toggleDialog={this.closeMaintenanceDialog}
              propertyTypes={this.props.propertyTypes}
            />
            {this.renderOutstandingMaintenanceTickets()}
          </Fragment>
        );
      }

      renderOutstandingMaintenanceTickets = () => {
        const maintenanceRequestsOptions = {
          serviceRequest: this.props.translate('maintenanceRequests'),
          requestForMaintenance: this.props.translate('maintenanceRequestsRFM'),
        };
        return (
          <Dialog
            disableBackdropClick
            fullScreen={this.props.fullScreen}
            open={this.props.isOpen}
            onClose={this.props.toggleDialog}
            aria-labelledby="create-maintenance-ticket"
            classes={{
              paper: 'form-dialog',
            }}
            id="maintenanceTicketsDialog"
          >
            <DialogTitle id="create-maintenance-ticket">
              <h3 style={{ margin: '10px 0 5px', textAlign: 'center' }}>
                {serviceRequestOptions(maintenanceRequestsOptions)}
                <b style={{ fontFamily: 'Circular-Black', color: AccentColor() }}>.</b>
              </h3>
            </DialogTitle>
            <DialogContent>
              <div style={{ borderTop: `5px solid ${AccentColor()}`, marginTop: '5px' }}>
                {this.renderMaintenanceRequests()}
                {this.renderLoadingMaintenanceRequests()}
                {this.renderNoMaintenanceRequests()}
              </div>
            </DialogContent>
            <DialogActions style={{ display: 'block' }}>{this.renderDialogActions()}</DialogActions>
          </Dialog>
        );
      };

      renderDialogActions = () => (
        <Fragment>
          <FlatButton
            onClick={this.props.toggleDialog}
            id="close-button"
            style={{ color: AccentColor(), textTransform: 'uppercase' }}
          >
            {this.props.translate('close')}
          </FlatButton>
        </Fragment>
      );

      renderNoMaintenanceRequests = () => {
        if (this.props.loadingTickets) return;
        if (this.props.maintenanceTickets.length > 0) return;

        const noRequestsOptions = {
          serviceRequest: this.props.translate('maintenanceRequests.messages.noRequests'),
          requestForMaintenance: this.props.translate('maintenanceRequests.messages.noRequestsRFM'),
        };

        const createRequestOptions = {
          serviceRequest: this.props.translate('maintenanceRequests.messages.createRequest'),
          requestForMaintenance: this.props.translate(
            'maintenanceRequests.messages.createRequestRFM'
          ),
        };

        return (
          <div style={{ textAlign: 'center', marginTop: '15px' }}>
            <p style={{ fontSize: '16px' }}>{serviceRequestOptions(noRequestsOptions)}</p>
            <p style={{ fontSize: '16px' }}>
              <button
                type="button"
                className="link-button"
                onClick={this.props.toggleNewMaintenanceDialog}
              >
                {serviceRequestOptions(createRequestOptions)}
              </button>
            </p>
          </div>
        );
      };

      renderLoadingMaintenanceRequests = () => {
        if (!this.props.loadingTickets) return;

        return (
          <div
            style={{
              textAlign: 'center',
              width: '60px',
              margin: '30px auto 15px',
            }}
          >
            <BounceLoader color={AccentColor()} />
          </div>
        );
      };

      renderMaintenanceRequests = () => {
        if (this.props.loadingTickets) return;

        const tickets = this.props.maintenanceTickets;
        const ticketsByStatus = groupBy(tickets, 'status');
        const activeTickets = tickets.filter((ticket) => !ticket.status.match(/Fixed|Closed/i));
        const closedTickets = tickets.filter((ticket) => ticket.status.match(/Fixed|Closed/i));
        const statuses = Object.keys(ticketsByStatus);

        if (tickets.length === 0) return;

        if (this.props.userPreferences.review_closed_maintenance_requests && statuses.length > 1) {
          return (
            <>
              <DialogSubHeaders>
                {getTranslatedString('tenantPortal.viewMaintenanceRequestsDialog.activeRequests')}
              </DialogSubHeaders>
              {activeTickets.map((ticket) => this.renderMaintenanceRequest(ticket))}
              <DialogSubHeaders>
                {getTranslatedString('tenantPortal.viewMaintenanceRequestsDialog.closedRequests')}
              </DialogSubHeaders>
              {closedTickets.map((ticket) => this.renderMaintenanceRequest(ticket))}
            </>
          );
        }

        if (
          statuses.length === 1 ||
          !this.props.userPreferences.review_closed_maintenance_requests
        ) {
          return tickets.map((ticket) => this.renderMaintenanceRequest(ticket));
        }
      };

      renderMaintenanceRequest = (ticket) => (
        <div
          id="mobile_card"
          key={ticket.id}
          style={{ borderBottom: `1px solid ${colors.LIGHT_GRAY}`, cursor: 'pointer' }}
          onClick={() => this.openMaintenanceDialog(ticket)}
        >
          <div className="row">
            <div className="mobile_header">
              {' '}
              <b
                style={{
                  fontFamily: 'Circular-Bold',
                  width: '350px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  display: 'block',
                  textOverflow: 'ellipsis',
                }}
              >
                {capitalize(ticket.subject || '')}
              </b>
            </div>
            <div style={{ float: 'right', marginTop: '4px' }}>
              <span style={{ fontSize: '14px', color: 'grey' }}>
                {timeFromNow(ticket.request_date, {
                  language: this.props.currentLanguage,
                })}
              </span>
              <span style={{ marginLeft: '10px' }}>
                {<TicketStatusIcon isTableLabel status={ticket.status} />}
              </span>
            </div>
          </div>
          <div className="row">
            <div className="mobile_subheader">
              <b style={{ fontFamily: 'Circular' }}>
                {ticket.ticket_number ? (
                  <span>
                    {this.props.translate('ticket')}
                    {' #'}
                    {ticket.ticket_number}
                  </span>
                ) : (
                  <span style={{ fontStyle: 'italic' }}>
                    {this.props.translate('requestCreatedPendingSync')}{' '}
                    <Icon component={LoadingOutlined} spin />
                  </span>
                )}
              </b>
            </div>
          </div>
          <RenderIfToggleOn toggle="ui.create_service_request.permission_to_enter">
            <div className="row">
              <div className="mobile_subheader">
                {this.props.translate('entryPermission')}{' '}
                <b style={{ fontFamily: 'Circular' }}>
                  (
                  {ticket.permission_to_enter
                    ? this.props.translate('request_type.yes')
                    : this.props.translate('request_type.no')}
                  )
                </b>
              </div>
            </div>
          </RenderIfToggleOn>
          {ticket.scheduled_at && (
            <div className="row">
              <div className="mobile_subheader completion_date">
                {this.props.translate('repairScheduledFor')}{' '}
                {toFriendlyDateString(ticket.scheduled_at, {
                  language: this.props.currentLanguage,
                })}
                {' - '}
                {ticket.scheduled_range_verbiage}
              </div>
            </div>
          )}
          {!hostIsBoardwalk() && (
            <div className="row">
              <div className="mobile_subheader">
                {this.props.translate('RFM.createdBy')} {ticket.created_by}
              </div>
            </div>
          )}
          <div className="row">
            <div className="mobile_subheader">
              <button
                type="button"
                className="link-button"
                onClick={() => this.openMaintenanceDialog(ticket)}
                style={{ cursor: 'pointer' }}
              >
                {this.props.translate('viewDetails')}
              </button>
            </div>
          </div>
        </div>
      );
    }
  )
);

export default localize()(OutstandingMaintenance);
